import React, { useCallback, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { propOr, propEq } from 'ramda'
import strings from 'utils/strings'
import { useSelector } from 'hooks/redux'
import useApi from 'hooks/useApi'
import {
  fetchSearchedCustomSwitches,
  addCustomGraphicsToSwitch,
} from 'actions/dash'
import { createModalErrorHandler } from 'sagas/errorHandlers'
import ModalErrorContent from 'components/patterns/ModalErrorContent'
import CustomSwitchGraphicsList from 'components/SwitchAndGauge/DashDrawer/CustomSwitchGraphicsList'
import SearchInputV2 from 'components/patterns/Inputs/SearchInputV2'
import { Header, Paragraph } from 'components/patterns/Typography'
import { Loading } from 'components/patterns'
import { getSelectedCustomGraphics } from 'selectors/dash'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalContent,
  ModalMainContainer,
  ModalCloseButton,
  ModalButton,
} from 'components/patterns/Modal'
import { themeColor } from 'utils/styles'
import withStyle, { StyledSearchBox } from './CustomSwitchGraphicsModal.style'

const CustomSwitchGraphicsModal = (props) => {
  const {
    isOpen,
    panelNumber,
    onClickConfirm,
    onClickCancel,
    isCustomIndicator,
  } = props

  const dispatch = useDispatch()
  const [searchedString, setSearchedInput] = useState('')
  const [isSearching, updateIsSearching] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isEmpty, setIsEmpty] = useState(false)
  // API error state — when set, modal body is replaced with ModalErrorContent
  const [modalError, setModalError] = useState(null)

  const [, doFetch] = useApi(fetchSearchedCustomSwitches, null)
  const selectedSwitchGraphics = useSelector(getSelectedCustomGraphics)

  // Clear the error state when the modal closes so that reopening shows the
  // normal search UI rather than the error body with the Confirm button disabled.
  useEffect(() => {
    if (!isOpen) setModalError(null)
  }, [isOpen])

  const handleConfirmClick = () => {
    onClickConfirm()
  }

  const handleOnChange = ({ target: { value } }) => {
    setSearchedInput(value)
  }

  const handleOnClear = () => {
    setSearchedInput('')
  }

  const handleOnSearch = () => {
    updateIsSearching(true)
    setIsLoading(true)
    setModalError(null)
    setIsEmpty(false)
    doFetch({
      searchedString,
      panelNumber,
      // createModalErrorHandler intercepts the API failure and sets modalError,
      // replacing the modal body with ModalErrorContent. The reject callback
      // below still fires (after the handler chain) to clear the loading state.
      errorHandlers: [createModalErrorHandler(setModalError)],
      resolve: (res) => {
        setIsLoading(false)
        // Guard against the de-dup path where res is undefined (a second search
        // with the same string+panelNumber while the first is in-flight resolves
        // with undefined). Without this guard, res.data.length would throw.
        const data = res?.data || []
        setIsEmpty(data.length === 0)
      },
      reject: () => {
        setIsLoading(false)
      },
    })
  }

  const foundSwitch = selectedSwitchGraphics.find(
    propEq('panelNumber', panelNumber)
  )

  const handleSwitchSelection = useCallback(
    (partNumber, description) => {
      dispatch(addCustomGraphicsToSwitch(partNumber, description, panelNumber))
    },
    [dispatch, panelNumber]
  )

  const isConfirmEnabled = Boolean(foundSwitch) && !modalError
  const selectedPartNumber = propOr(null, 'partNumber', foundSwitch)

  if (!isOpen) return null
  const headerText = isCustomIndicator
    ? strings.custom_indicator_graphics
    : strings.custom_switch_graphics

  return (
    <Modal>
      <ModalHeader>
        <Header type="h2">{headerText}</Header>
        <ModalCloseButton onClick={onClickCancel} />
      </ModalHeader>
      <ModalBody>
        <ModalMainContainer>
          <ModalContent style={{ minHeight: '50vh' }}>
            {modalError ? (
              <ModalErrorContent />
            ) : (
              <>
                <StyledSearchBox>
                  <Paragraph size="small">
                    You can search by the cap title or part number.
                    {isCustomIndicator && (
                      <>
                        <br /> The indicator cap color is determined by the
                        indicator base.
                      </>
                    )}
                  </Paragraph>
                  <SearchInputV2
                    value={searchedString}
                    onChange={handleOnChange}
                    onClear={handleOnClear}
                    onSearch={handleOnSearch}
                    placeholder={strings.enter_cap_part_number_or_title}
                    width="250"
                  />
                </StyledSearchBox>
                {isLoading && <Loading />}
                {isEmpty && (
                  <Paragraph
                    style={{
                      textAlign: 'center',
                      color: themeColor('blackSecondary'),
                    }}
                  >
                    {strings.no_results_returned}
                  </Paragraph>
                )}
                {isSearching && !isLoading && !isEmpty && (
                  <CustomSwitchGraphicsList
                    panelNumber={panelNumber}
                    onSwitchSelection={handleSwitchSelection}
                    selectedPart={selectedPartNumber}
                  />
                )}
              </>
            )}
          </ModalContent>
        </ModalMainContainer>
      </ModalBody>
      <ModalFooter>
        <ModalButton type="secondary" onClick={onClickCancel}>
          {strings.cancel}
        </ModalButton>
        <ModalButton
          type="primary"
          onClick={handleConfirmClick}
          disabled={!isConfirmEnabled || !!modalError}
        >
          {strings.confirm}
        </ModalButton>
      </ModalFooter>
    </Modal>
  )
}

CustomSwitchGraphicsModal.propTypes = {
  isOpen: PropTypes.bool,
  onClickCancel: PropTypes.func,
  onClickConfirm: PropTypes.func,
  panelNumber: PropTypes.string,
  isCustomIndicator: PropTypes.bool,
}

export default withStyle(CustomSwitchGraphicsModal)
